/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Typography, Box } from '@mic3/platform-ui';

import Loader from 'app/components/atoms/Loader/Loader';

import { loadTeams, addTeamUsers, removeTeamUsers, loadUserAboutTeams } from 'store/actions/admin/teamsActions';

import { bind, debounce } from 'app/utils/decorators/decoratorUtils';
import { getPermissions } from 'app/config/rolesConfig';
import Immutable from 'app/utils/immutable/Immutable';
import { isEmpty } from 'app/utils/utils';
import LazySelectionList from 'app/containers/Common/SelectionList/LazySelectionList';

const SelectionListWrapper = styled(Box)`
    .MuiExpansionPanelSummary-root.Mui-expanded {
        min-height: 0 !important;
    }
    .MuiExpansionPanelDetails-root {
        margin-top: 0 !important;
        padding-bottom:0 !important;
    }
    .button-wrapper{
        margin-right: 10px;
    }
    height: 100%;
`;

class UserTeams extends PureComponent {
    static propTypes = {
        user: PropTypes.object, // user details
        isLoading: PropTypes.bool,
        loadTeams: PropTypes.func,
        addTeamUsers: PropTypes.func,
        removeTeamUsers: PropTypes.func,
    };

    listRef = React.createRef();
    defaultStartStopIndex = { startIndex: 0, stopIndex: 30 };

    constructor(props) {
        super(props);
        this.state = Immutable({
            isUpdatingUserTeams: false,
            listKey: 0,
        });
        this.defaultState = this.state;
    }

    componentDidUpdate(prevProps) {
        const { user: { id: prevId } = {} } = prevProps || {};
        const { user: { id: newId } = {} } = this.props || {};
        if (prevId !== newId) {
            this.loadUserTeams();
        }
    }
    
    @bind
    async loadUserTeams(options) {
        const { user } = this.props;
        const { startIndex, stopIndex, filterBy = [] } = options || {};
        await this.props.loadTeams({ 
            options: { 
                filterBy: [...filterBy, { field: 'users.user.id', op: '=', value: user?.id }],
                startIndex,
                stopIndex,
            },
        });
    }

    @bind
    loadUserAboutTeams(){
        const { user, loadUserAboutTeams } = this.props;
        return loadUserAboutTeams({ options: { filterBy: [{ field: 'users.user.id', op: '=', value: user?.id }] } })
    }

    @bind
    setDefaultState() {
        this.setState(this.defaultState);
    }

    @bind
    @debounce()
    refreshList() {
        this.listRef.current?.forceUpdate();
        this.setState({ isUpdatingUserTeams: false, listKey: this.state.listKey + 1, });
    }

    @bind
    async onSubmitTeams(newTeams) {
        const { user: { id: userId } = {}, teams: existingTeams, addTeamUsers } = this.props || {};
        if (!userId || isEmpty(newTeams)) {
            return;
        }
        this.setState({isUpdatingUserTeams: true})
        const addedPromises = newTeams.map(async team => {
            if (!existingTeams.find(t => t.id === team.id)) {
                await addTeamUsers(team.id, [userId]);
            }
        }, []);
        await Promise.all(addedPromises).then(() => {
            this.loadUserTeams();
            this.loadUserAboutTeams();
            this.refreshList();
        });
    }

    @bind
    async removeTeams(selectedTeamsListIds) {
        const { user: { id: userId } = {}, removeTeamUsers } = this.props || {};
        if (!userId || isEmpty(selectedTeamsListIds)) {
            return;
        }
        this.setState({isUpdatingUserTeams: true})
        const removePromises = selectedTeamsListIds.map(async teamId => {
            await removeTeamUsers(teamId, [userId]);
        });
        await Promise.all(removePromises).then(() => {
            this.loadUserTeams();
            this.loadUserAboutTeams();
            this.refreshList();
        });
    }

    render() {
        const { user, teams, isLoading, totalRecords, startIndex } = this.props || {};
        const { isUpdatingUserTeams, listKey } = this.state;
        const { canEdit } = getPermissions(user.role);
        const filterBy = [{ field: 'active', op: '=', value: true }];
        const excludeBy = [{ field: 'users.user.id', op: '=', value: user?.id }];
        return (
            <>
                {isUpdatingUserTeams && <Loader absolute backdrop />}
                <SelectionListWrapper>
                    <LazySelectionList
                        key={listKey}
                        list={teams}
                        ref={this.listRef}
                        type="team"
                        typeChild="workspace"
                        onAdd={this.onSubmitTeams}
                        onRemove={this.removeTeams}
                        isLoading={isLoading}
                        totalRecords={totalRecords}
                        startIndex={startIndex}
                        loadData={this.loadUserTeams}
                        userRole={user.role}
                        disabled={!canEdit}
                        showChildRoles
                        filterBy={filterBy}
                        excludeBy={excludeBy}
                        fullHeight
                        showSearch
                        expansionDetailCaption={
                            <Box p={2}>
                                <Typography variant="body2" color="textSecondary">
                                    User has access to the following workspaces and permissions through the team.
                                </Typography>
                            </Box>
                        }
                    />
                </SelectionListWrapper>
            </>
        );
    }
}

export default connect(
    state => ({
        isLoading: state.admin.teams.list.isLoading,
        teams: state.admin.teams.list.records,
        totalRecords: state.admin.teams.list.count,
        startIndex: state.admin.teams.list.startIndex,
    }),
    {
        loadTeams,
        addTeamUsers,
        removeTeamUsers,
        loadUserAboutTeams,
    }
)(UserTeams);
